/* eslint-disable jsdoc/require-jsdoc */
import { toHexFromId } from '#~/api/index.js';
import type { Brand, Model } from '@studio/models/device';
import type { Registrant } from '#~/api/device/registry/index.js';
import type { Discovery$Control } from '#~/device/discovery/index.js';
import type { Device } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';
import { logger } from '#~/logger/index.js';
import type { PbDevice, PbStatusDevice, PbStatusDiscovery, PbStudioDevice } from './types.js';
import { pbToJsPtz, pbToJsPtzPosition } from './utils.js';
import { clamp } from '@studio/js-utils/range';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { toCredentials } from '@studio/models/auth';
import {
  ControlDefaults,
  Protocol,
  toConfiguration,
  toProtocol,
  toUri
} from '@studio/models/control';
import type { Control } from '@studio/models/control';
import { toStatus as toCalibrationStatus } from '@studio/models/control/calibration';
import { toStatus as toMovementStatus } from '@studio/models/control/movement';
import { toConfiguration as toOnvifConfig } from '@studio/models/control/onvif';
import { Limits, PresetDefaults } from '@studio/models/control/preset';
import type { Group, Preset } from '@studio/models/control/preset';
import { Headers } from '@studio/models/control/visca-ip';
import { formatUri, parseUri, toPort, toTransport } from '@studio/models/net';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type {
  StudioDeviceControlPreset,
  StudioDeviceControlPresetGroup
} from '@tryiris-ai/common.protojs/studio/v1/studio';
import { isEmpty, isNotNil, toNumber, toString } from '@tryiris-ai/js-types';
import type { T_Maybe, T_NotNil, T_Optional } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import { jsNativeTargetToPb, pbNativeTargetToJs } from './preset-target.js';

type PbStudioDeviceControl = T_NotNil<PbStudioDevice['control']>;

/* JS => PB */

const jsToPbStudioDeviceControlPresets = (
  presets: Control['presets'],
  deviceId: Device['id']
): PbStudioDeviceControl['presets'] => {
  const groups = Object.fromEntries(
    Object.values(presets?.groups ?? {}).map(
      (group): [string, DeepPartial<StudioDeviceControlPresetGroup>] => [
        group.id,
        {
          name: group.name.slice(0, Limits.maxNameLength),
          rank: group.rank
        }
      ]
    )
  );

  // There needs to be at least one group!
  if (isEmpty(groups)) {
    // Derive the default key from device identity so the same camera
    // re-serializes to one group; generateId() would mint a new key each call
    // and state-sync's per-key merge would accumulate empty "Group 1"s.
    groups[deviceId] = {
      name: `${PresetDefaults.groupName} 1`,
      rank: 0
    };
  }

  const items = Object.fromEntries(
    Object.values(presets?.items ?? {})
      .map((item): T_Maybe<[string, DeepPartial<StudioDeviceControlPreset>]> => {
        // Does the preset group exist?
        if (!groups[item.groupId]) {
          return undefined;
        }

        return [
          item.id,
          {
            group: item.groupId,
            name: item.name.slice(0, Limits.maxNameLength),
            rank: item.rank,
            thumbnail: item.thumbnail,
            nativeTarget: jsNativeTargetToPb(item.nativeTarget),
            requiredAxes: item.requiredAxes,
            position: item.position
              ? {
                  pan: item.position.pan,
                  tilt: item.position.tilt,
                  zoom: item.position.zoom
                }
              : undefined
          }
        ];
      })
      .filter(isNotNil)
  );

  return { groups, items };
};

export const jsToPbStudioDeviceAutoTracking = (
  device: T_Optional<Device>
): PbStudioDevice['autoTrackingControl'] => {
  const { autoTracking } = device;

  return {
    safePosition: autoTracking?.safePosition
      ? {
          pan: autoTracking.safePosition.pan,
          tilt: autoTracking.safePosition.tilt,
          zoom: autoTracking.safePosition.zoom
        }
      : undefined,
    trackingSpeed: autoTracking?.trackingSpeed,
    zoomAdaptive: autoTracking?.zoomAdaptive,
    zoomManual: autoTracking?.zoomManual
  };
};

export const jsToPbStudioDeviceControl = (
  device: T_Optional<Device>
): T_Maybe<PbStudioDeviceControl> => {
  const { control, id, modelId } = device;

  if (!id || !modelId || !control) {
    return undefined;
  }

  return {
    presets: jsToPbStudioDeviceControlPresets(control.presets, id),
    uri: formatUri(toUri(control.config, { modelId }))
  };
};

/* PB => JS */

export const pbToJsAutoTracking = (
  pbAutoTracking: T_Maybe<PbStudioDevice['autoTrackingControl']>
): Control['autoTracking'] => {
  const { safePosition, trackingSpeed, zoomAdaptive, zoomManual } = pbAutoTracking ?? {};

  return {
    safePosition: pbToJsPtz(safePosition),
    trackingSpeed,
    zoomAdaptive,
    zoomManual
  };
};

export const pbToJsControlCalibration = (
  pbCalibration: PbStatusDevice['calibration']
): Control['calibration'] => {
  const obj = pbCalibration?.status;

  // Per absent-data-gates-permissively (ai.knowledge/global): gate on the
  // PRESENCE of the reported status object, not on the derived enum. During the
  // add/reload window the encoder has not published a status subtree yet, so
  // `obj` is genuinely absent (nullish) — map that to undefined ("unknown").
  // toStatus() coerces an absent state to CALIBRATION_STATE_NULL (its zero
  // value), so the derived status is never nil and cannot tell "unknown yet"
  // from an explicitly reported NULL ("loaded, never calibrated"); keying off
  // obj's presence keeps the two apart so the badge only nags on a real status.
  return isNotNil(obj)
    ? {
        error: obj.error,
        progress: Math.round(clamp(toNumber(obj.progress, { default: 0 }) * 100, [0, 100])),
        status: toCalibrationStatus(obj.state)
      }
    : undefined;
};

const pbToJsControlCapabilitiesDiscovery = (
  pbCapabilities: PbStatusDevice['capabilities']
): Control['capabilities'] => {
  const uri = parseUri(pbCapabilities?.video?.discovered?.uri);

  return uri ? { discovery: { uri } } : undefined;
};

const pbToJsControlCapabilitiesMovement = (
  pbCapabilities: PbStatusDevice['capabilities']
): Control['capabilities'] => {
  const { movement, overview } = pbCapabilities ?? {};

  if (!overview?.hasMovement || !movement?.vectors || isEmpty(movement.vectors)) {
    return undefined;
  }

  const { focus, pan, tilt, zoom } = movement.vectors;

  return {
    focus: focus?.enabled
      ? {
          absolute: !!focus.absoluteMove,
          continuous: !!focus.continuousMove,
          onePush: !!focus.onePush,
          position: !!focus.getPosition
        }
      : undefined,
    pan: pan?.enabled
      ? {
          absolute: !!pan.absoluteMove,
          continuous: !!pan.continuousMove,
          position: !!pan.getPosition
        }
      : undefined,
    tilt: tilt?.enabled
      ? {
          absolute: !!tilt.absoluteMove,
          continuous: !!tilt.continuousMove,
          position: !!tilt.getPosition
        }
      : undefined,
    zoom: zoom?.enabled
      ? {
          absolute: !!zoom.absoluteMove,
          continuous: !!zoom.continuousMove,
          position: !!zoom.getPosition
        }
      : undefined
  };
};

const pbToJsControlCapabilities = (
  pbCapabilities: PbStatusDevice['capabilities']
): Control['capabilities'] => {
  const discovery = pbToJsControlCapabilitiesDiscovery(pbCapabilities);
  const movement = pbToJsControlCapabilitiesMovement(pbCapabilities);

  return [discovery, movement].some(isNotNil) ? { ...discovery, ...movement } : undefined;
};

const pbToJsControlMovement = (pbDevice: PbDevice): Control['movement'] => {
  const { pbControl, pbStatus } = pbDevice;

  const pbControlMovement = pbControl?.movement;
  const pbStatusMovement = pbStatus?.movement;

  return {
    error: toString(pbStatusMovement?.status?.error),
    focus: pbStatusMovement?.position?.focus ?? 0,
    position: pbToJsPtz(pbStatusMovement?.position),
    quickShots: pbToJsPtz(pbControlMovement?.quickShots),
    status: toMovementStatus(pbStatusMovement?.status?.state)
  };
};

export const pbToJsControlPresets = (
  pbPresets: PbStudioDeviceControl['presets']
): Control['presets'] => {
  const groups = Object.fromEntries(
    Object.entries(pbPresets?.groups ?? {})
      .map(([key, pbGroup]): T_Maybe<[Group['id'], Group]> => {
        const pbValue = pbGroup;

        if (!pbValue) {
          return undefined;
        }

        const id = normalizeId<Group['id']>(key);
        const name = toString(pbValue.name)?.slice(0, Limits.maxNameLength);
        const rank = toNumber(pbValue.rank, { default: Number.MAX_SAFE_INTEGER });

        if (!id || !name) {
          return undefined;
        }

        return [
          id,
          {
            id,
            name,
            rank
          }
        ];
      })
      .filter(isNotNil)
  );

  const items = Object.fromEntries(
    Object.entries(pbPresets?.items ?? {})
      .map(([key, pbPreset]): T_Maybe<[Preset['id'], Preset]> => {
        const pbValue = pbPreset;

        if (!pbValue) {
          return undefined;
        }

        const id = normalizeId<Preset['id']>(key);
        const groupId = normalizeId<Group['id']>(pbValue.group);
        const name = toString(pbValue.name)?.slice(0, Limits.maxNameLength);
        const nativeTarget = pbNativeTargetToJs(pbValue.nativeTarget);
        const hasPositionAxis =
          pbValue.position?.pan != null ||
          pbValue.position?.tilt != null ||
          pbValue.position?.zoom != null;
        const rank = toNumber(pbValue.rank, { default: Number.MAX_SAFE_INTEGER });
        const thumbnail = toString(pbValue.thumbnail);
        // Native-only records keep nativeTarget without inventing a zero pose
        // so a later jsToPb of this model still serializes the mapping.
        if (nativeTarget && !hasPositionAxis) {
          if (!groupId || !id || !name || !groups[groupId]) {
            return undefined;
          }
          return [
            id,
            {
              groupId,
              id,
              name,
              nativeTarget,
              rank,
              thumbnail
            }
          ];
        }
        const position = pbToJsPtzPosition(pbValue.position);

        if (!groupId || !id || !name || !groups[groupId]) {
          return undefined;
        }

        return [
          id,
          {
            groupId,
            id,
            name,
            nativeTarget,
            position,
            requiredAxes: pbValue.requiredAxes,
            rank,
            thumbnail
          }
        ];
      })
      .filter(isNotNil)
  );

  return {
    groups,
    items
  };
};

export const pbToJsControl = (pbDevice: PbDevice): T_Maybe<Control> => {
  const { pbStatus, pbStudio } = pbDevice;

  const config = toConfiguration(parseUri(pbStudio?.control?.uri));

  if (!config) {
    return undefined;
  }

  return defaults(
    { config },
    {
      autoTracking: pbToJsAutoTracking(pbStudio?.autoTrackingControl),
      calibration: pbToJsControlCalibration(pbStatus?.calibration),
      capabilities: pbToJsControlCapabilities(pbStatus?.capabilities),
      movement: pbToJsControlMovement(pbDevice),
      presets: pbToJsControlPresets(pbStudio?.control?.presets)
    },
    ControlDefaults
  );
};

export const pbToJsControlTemplate = ({ config }: Registrant): Template['control'] => {
  const protocol = toProtocol(config.ptzDriver);

  if (!protocol) {
    return undefined;
  }

  const port = toPort(config.ptzPort);

  if (protocol === Protocol.ONVIF) {
    return {
      credentials: toCredentials({
        password: config.ptzPassword,
        username: config.ptzUsername
      }),
      port,
      protocol
    };
  }

  if (protocol === Protocol.HTTP) {
    return {
      credentials: toCredentials({
        password: config.ptzPassword,
        username: config.ptzUsername
      }),
      port,
      protocol
    };
  }

  if (protocol === Protocol.VISCA_IP) {
    return {
      headers: config.ptzHeaders ? Headers.Extended : Headers.Standard,
      port,
      protocol,
      transport: toTransport(config.ptzProtocol)
    };
  }

  logger.warning(`@fixme auto-config: ${protocol}`);

  return undefined;
};

export const pbToJsDiscoveryControl = (
  agentId: Agent['id'],
  pbDiscovery: PbStatusDiscovery
): T_Maybe<Omit<Discovery$Control, 'id'>> => {
  const { network, source } = pbDiscovery;

  if (source && network) {
    const protocol = toProtocol(source);

    if (!protocol) {
      return undefined;
    }

    if (protocol === Protocol.ONVIF) {
      const control = toOnvifConfig(parseUri(pbDiscovery.meta));
      const model = toString(pbDiscovery.model);
      // `brand` is on the wire (StatusDiscovery.brand) and was read by nobody,
      // so every ONVIF row showed a bare model string like "ln" with no maker
      // attached (ENG-3796). Carried through as its own field rather than
      // folded into `name`, which the Add Video Source table renders verbatim.
      const brand = toString(pbDiscovery.brand);
      const modelId = pbDiscovery.modelId?.length
        ? toHexFromId<Model['id']>(pbDiscovery.modelId)
        : undefined;
      const brandId = pbDiscovery.brandId?.length
        ? toHexFromId<Brand['id']>(pbDiscovery.brandId)
        : undefined;

      return control
        ? {
            agentId,
            control,
            name: model ? `${model} (${control.host})` : control.host,
            ...(brand ? { brand } : {}),
            ...(model ? { modelName: model } : {}),
            // Identification is independent of the advertised control transport.
            ...(modelId ? { modelId } : {}),
            ...(brandId ? { brandId } : {})
          }
        : undefined;
    }
  }

  return undefined;
};
